'use client';

import type { ExclusiveZone } from '@/types/zones/exclusive-zone';
import { ExclusivePreLaunchBackground } from './subcomponents/background';
import { ExclusivePreLaunchTrailerPortrait, ExclusivePreLaunchTrailerLandscape } from './subcomponents/trailer';
import { ExclusivePreLaunchLogo } from './subcomponents/logo';
import { ExclusivePreLaunchContentContainer } from './subcomponents/content-container';
import { EXCLUSIVE_ZONE_NAMES, ORIENTATIONS, SLICE_TYPE, EXCLUSIVE_LAUNCH_TYPE } from '@/utils/constants';
import { useDeviceOrientation } from '@/hooks/use-device-orientation';
import { useExclusivePopupTrigger } from '@/hooks/use-exclusive-popup-trigger';
import { useStore } from '@/store/store';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useEffect } from 'react';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { classes } from '@/utils/classes/classes';
import BadgeFactoryClient from '@/app/components/badge/badge-factory.client';
import { TextBadge } from '@/app/components/badge/layouts/text/text-badge';
import { getGameName } from '@/logic/game-adapter/game-adapter';
import { validatePreLaunchContent } from '@/utils/exclusive-popup/validate-content';

import './styles/default.scss';
import { getText } from '@/utils/text/text';

type ExclusivePreLaunchZoneProps = {
    zone: ExclusiveZone;
    isMobileDevice: boolean;
};

export const ExclusivePreLaunchZone: React.FC<ExclusivePreLaunchZoneProps> = ({ zone, isMobileDevice }) => {
    const layoutVariant = useDeviceOrientation({
        initialIsMobile: isMobileDevice
    });
    const { featureFlags } = useGlobalContext();
    const isEarlyAccess = featureFlags[PRODUCT_THROTTLES.HAS_EARLY_ACCESS];

    const validated = validatePreLaunchContent(zone, layoutVariant);
    const zoneContent = zone?.data;
    const earlyAccessZone = zoneContent?.exclusive_launch_type === EXCLUSIVE_LAUNCH_TYPE.EARLY_ACCESS;

    const setReminder = useStore((state) => state.reminders.setReminder);

    useEffect(() => {
        if (zone?.is_reminder_set !== undefined && zone?.uid) {
            setReminder(zone.uid, zone.is_reminder_set);
        }
    }, [zone?.uid, zone?.is_reminder_set, setReminder]);

    const showPopup = useExclusivePopupTrigger({
        zoneData: zoneContent,
        zoneUid: zone?.uid ?? '',
        isMobile: isMobileDevice,
        exclusiveType: SLICE_TYPE.EXCLUSIVE_PRE_LAUNCH,
        exclusiveLaunchType: zoneContent?.exclusive_launch_type || ''
    });

    if (!validated) {
        return null;
    }

    const { layoutContent, gameContent, backgroundUrl } = validated;
    const gameData = gameContent.data;
    const gameUid = gameContent.uid;
    const gameName = getGameName(gameData.display_name);

    return (
        <div
            className={classes(
                'exclusive-pre-launch',
                `exclusive-pre-launch--${layoutVariant}`,
                earlyAccessZone && 'early-access-zone'
            )}
            id={'exclusive-pre-launch'}
            data-t-prepostlaunch="pre-launch"
            data-t-zonename={
                earlyAccessZone ? EXCLUSIVE_ZONE_NAMES.PRE_LAUNCH_EARLY_ACCESS : EXCLUSIVE_ZONE_NAMES.PRE_LAUNCH
            }
            data-t-gameuid={gameUid}
            data-t-gamename={gameName}
            data-t-gameposition={1}>
            {earlyAccessZone && (
                <BadgeFactoryClient>
                    <TextBadge label={getText({ text: 'components.badge.earlyAccess' })} />
                </BadgeFactoryClient>
            )}
            <ExclusivePreLaunchBackground layoutVariant={layoutVariant} url={backgroundUrl} />
            <div className="exclusive-pre-launch__content">
                <div className="exclusive-pre-launch__inner">
                    {layoutVariant === ORIENTATIONS.PORTRAIT ? (
                        <ExclusivePreLaunchTrailerPortrait
                            openModalButtonUrl={layoutContent.primary.open_modal_button?.url}
                            handleWidgetClick={showPopup}
                        />
                    ) : (
                        <ExclusivePreLaunchTrailerLandscape
                            url={zoneContent?.video_trailer?.url}
                            id={layoutContent.id}
                            gameUid={gameUid}
                            poster={layoutContent.primary.video_loading_placeholder?.url}
                        />
                    )}
                    <ExclusivePreLaunchContentContainer
                        layoutVariant={layoutVariant}
                        zoneUid={zone?.uid ?? ''}
                        gameId={gameUid || ''}
                        gameName={gameName || ''}
                        content={{
                            ...layoutContent.primary,
                            launchDate: zoneContent?.launch_date,
                            category: zoneContent?.category
                        }}
                    />
                </div>
                {layoutVariant === ORIENTATIONS.PORTRAIT && (
                    <ExclusivePreLaunchLogo logoUrl={layoutContent.primary.logo?.url} isEarlyAccess={isEarlyAccess} />
                )}
                {gameData.copyright_text && (
                    <div className={`exclusive-pre-launch__copyright--${layoutVariant}`}>{gameData.copyright_text}</div>
                )}
            </div>
        </div>
    );
};
